import React from 'react';

const TIME_REGEX = /^\d{2}:\d{2}$/;

export interface AppInputTimeProps {
  /** HH:MM string, or empty when unset. */
  value: string;
  /** Receives the raw HH:MM string from the native input. */
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  /** When true, applies the red error border (matches AppForm.Item error state). */
  error?: boolean;
  /**
   * CSS color used for the focused border. Pass the active theme primary so
   * the input matches other inputs on the page. Defaults to Tailwind blue-500.
   */
  focusColor?: string;
  /**
   * On blur, if the value isn't a valid HH:MM string, clear it. Defaults to
   * true — browsers normally emit HH:MM, but partially-typed input via
   * keyboard or unsupported browsers can leave a malformed string.
   */
  normalizeOnBlur?: boolean;
  id?: string;
  className?: string;
}

/**
 * Native `<input type="time">` styled to match the project's atomic inputs.
 * Used by the massager 訪問追加 / 削除 forms (HIT-2329); kept as an atom so
 * any other form needing a time picker can reuse the same look + error
 * border behavior.
 *
 * Wrap inside `<AppForm.Item>` for the help/error message line; this
 * component only renders the input itself + its border state.
 */
const AppInputTime = React.forwardRef<HTMLInputElement, AppInputTimeProps>(
  (
    {
      value,
      onChange,
      onBlur,
      disabled,
      error,
      focusColor,
      normalizeOnBlur = true,
      id,
      className,
    },
    ref,
  ) => {
    const handleBlur = () => {
      if (normalizeOnBlur && value && !TIME_REGEX.test(value)) {
        onChange('');
      }
      onBlur?.();
    };

    /**
     * OPEN THE DROPDOWN ON CLICK, because the icon cannot do it.
     *
     * index.scss sets `appearance: none` on every time input — needed to stop iOS centring the value and
     * to paint a clock that matches the AntD pickers used elsewhere. Chrome's answer to that is to stop
     * rendering its picker button altogether, so the clock on screen is a background image with nothing
     * behind it. Reported as 「bấm vào đồng hồ không ra dropdown」, and no amount of CSS on
     * ::-webkit-calendar-picker-indicator fixes it: the element is not there to style.
     *
     * showPicker() asks the browser directly and does not care about `appearance`. It must run inside a
     * user gesture — a click is one — and it throws where it is unsupported or not allowed, which is why
     * the call is guarded rather than assumed. Failing quietly leaves today's behaviour (type it in),
     * which is what every browser without showPicker() gives anyway.
     */
    const openPicker = (e: React.MouseEvent<HTMLInputElement>) => {
      const input = e.currentTarget;
      if (disabled || typeof input.showPicker !== 'function') return;
      try {
        input.showPicker();
      } catch {
        // Unsupported / blocked — the field is still typeable, so there is nothing to report.
      }
    };

    const borderClass = error ? 'border-red-500' : 'border-gray-300';

    return (
      <input
        ref={ref}
        id={id}
        type="time"
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        onClick={openPicker}
        onBlur={handleBlur}
        disabled={disabled}
        style={focusColor ? { ['--focus-color' as string]: focusColor } : undefined}
        className={`h-10 w-full min-w-0 rounded-md border bg-white px-3 text-sm outline-none transition-colors focus:border-[var(--focus-color)] disabled:bg-gray-50 disabled:text-gray-500 ${borderClass} ${className ?? ''}`}
      />
    );
  },
);

AppInputTime.displayName = 'AppInputTime';

export default AppInputTime;
